import { useState } from 'react';

import LoaderButton from 'components/Button/LoaderButton';
import { RegisterImplementationProps } from 'constants/types';
import {
  AuthorizeContextValue,
  withAuthorizeContext,
} from 'pages/Authorize/AuthorizeContext';
import { AnalyticsEventName, track } from 'utils/analytics';
import { useOAuthStatePolling } from '../../OAuth/useOAuthStatePolling';
import SignInBase from '../SignInBase';
import { getProviderFormLabel } from '../utils';

// Shape of provider.oauthCredentials in the validate response. every.io runs
// an inverted handshake: the employer holds no credential, so this carries no
// secret — `clientId` is the partner slug every.io knows Finch by, sent as the
// `partner` query param. The backend exchanges the returned code using the
// shared partner key. The installed common types predate these.
export type EveryOAuthCredentials = {
  authorizeUrl?: string | null;
  clientId?: string | null;
  redirectUri?: string | null;
};

export const getOAuthUrl = ({
  config,
  sessionKey,
}: {
  config: EveryOAuthCredentials | null;
  sessionKey: string;
}) => {
  if (!config?.authorizeUrl || !config?.clientId || !config?.redirectUri) {
    throw new Error('Invalid OAuth config');
  }

  let url: URL;
  try {
    url = new URL(config.authorizeUrl);
  } catch {
    throw new Error('Invalid OAuth config');
  }

  // Per the agreed handshake protocol: `partner` identifies Finch, `state` is
  // the opaque Connect session key every.io echoes back verbatim (the bare
  // key, not the JSON form other sign-ins use; the callback hook accepts
  // either), and `redirect_uri` is validated by every.io against an allowlist.
  url.searchParams.set('partner', config.clientId);
  url.searchParams.set('redirect_uri', config.redirectUri);
  url.searchParams.set('state', sessionKey);

  return url.toString();
};

const RegisterEveryOAuth = ({
  provider,
  onSubmit,
  setError,
  sessionKey,
  onMockOAuth,
}: RegisterImplementationProps & AuthorizeContextValue) => {
  const [buttonLoading, setButtonLoading] = useState(false);

  const config = provider.oauthCredentials as EveryOAuthCredentials | null;

  const onCodeReceived = async (clientCode: string) => {
    // The code is all the backend needs: the exchange runs server-to-server
    // with the shared partner key and takes no redirect_uri.
    await onSubmit({ clientCode });
  };

  const { loading, openOAuthWindow } = useOAuthStatePolling({
    sessionKey,
    onCodeReceived,
    setError,
  });

  const onOAuthClick = async () => {
    setButtonLoading(true);
    const didMockOAuth = await onMockOAuth();

    if (didMockOAuth) {
      setButtonLoading(false);
      return;
    }

    const oauthUrl = getOAuthUrl({ config, sessionKey });

    track(AnalyticsEventName.RedirectedToOAuthUrl, {
      oauthUrl,
    });

    openOAuthWindow(oauthUrl);
    setButtonLoading(false);
  };

  const instructions = (
    <p>
      Every authentication will continue in a new window. Please make sure to
      allow popups, then sign in to Every and approve the connection. You will
      need to be an Every admin to complete this step.
    </p>
  );

  return (
    <SignInBase
      formLabel={getProviderFormLabel(provider)}
      instructions={instructions}
    >
      <div
        style={{
          marginTop: 'auto',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <LoaderButton
          isLoading={loading || buttonLoading}
          disabled={loading || buttonLoading}
          onClick={onOAuthClick}
          text="Connect"
        />
      </div>
    </SignInBase>
  );
};

export default withAuthorizeContext(RegisterEveryOAuth);
